import { createContext, useCallback, useEffect, useRef } from "react";
import { useAuth as useOidcAuth } from "react-oidc-context";

import { EnvVariables } from "@/env-variables";
import { revokeRefreshToken } from "@/services/auth";
import LocalStorage from "@/storages/LocalStorage";
import { type AuthParamsSnapshot, cleanAuthUrl, readAuthParamsFromLocation } from "./authParams";
import { SSO_AUTH_PROGRESS_KEY } from "./constants";
import { useAuthInitialization, useAuthMethods, useAuthState, useRedirectPath } from "./hooks";
import {
  readAuthReturnPathFromState,
  requireAdditionalInfoOnNextLogin,
  saveAuthReturnPath,
} from "./redirect";
import {
  type AuthContextType,
  type AuthProviderProps,
  defaultAuthContext,
  type User,
} from "./types";

export const AuthContext = createContext<AuthContextType>(defaultAuthContext);

/** Upper bound on how long sign-out waits for refresh-token revocation before navigating. */
const REVOKE_TIMEOUT_MS = 1500;

const wait = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

const hasValidStoredUserSession = () => {
  const storedUser = sessionStorage.getItem("user");
  if (!storedUser) return false;

  try {
    JSON.parse(storedUser);
    return true;
  } catch {
    return false;
  }
};

export const AuthConsumer = ({
  children,
  cognitoAuthConfig,
}: {
  children: React.ReactNode;
  cognitoAuthConfig: AuthProviderProps["cognitoAuthConfig"];
}) => {
  const oidcAuth = useOidcAuth();

  const {
    user,
    loading,
    setLoading,
    performLogoutCleanup,
    handlePostLoginInitialization,
    restoreUserSession,
    saveUserSession,
  } = useAuthState();

  const { saveRedirectPath, getRedirectPath, safeRedirect } = useRedirectPath();

  const { handleTokenExchange, handleOidcLogin, handleManualLogin, handleTpassLogin } =
    useAuthMethods({
      saveUserSession,
      handlePostLoginInitialization,
      getRedirectPath,
      safeRedirect,
    });

  const processAuthParams = useCallback(
    async (authParams: AuthParamsSnapshot = readAuthParamsFromLocation()) => {
      if (authParams.hasSsoRequest) {
        saveRedirectPath();
        requireAdditionalInfoOnNextLogin(undefined, { force: true });
        sessionStorage.setItem(SSO_AUTH_PROGRESS_KEY, "true");
        await oidcAuth.signinRedirect();
        return true;
      }

      if (authParams.hasOidcCallback) {
        sessionStorage.setItem(SSO_AUTH_PROGRESS_KEY, "true");
        return true;
      }

      if (authParams.hasTpassCallback) {
        // Set progress flag for TPass login to prevent race condition
        sessionStorage.setItem(SSO_AUTH_PROGRESS_KEY, "tpass");

        const stateReturnPath = readAuthReturnPathFromState(authParams.state);
        if (stateReturnPath) {
          saveAuthReturnPath(stateReturnPath);
        }

        cleanAuthUrl();

        return await handleTokenExchange(
          authParams.userParam,
          authParams.sessionParam,
          authParams.ivParam,
        );
      }

      return false;
    },
    [oidcAuth, handleTokenExchange, saveRedirectPath],
  );

  const logout = useCallback(() => {
    saveRedirectPath();
    // No navigation follows, so the request can finish in the background. The refresh token is
    // read synchronously here, before performLogoutCleanup() clears it.
    void revokeRefreshToken();
    performLogoutCleanup();
  }, [saveRedirectPath, performLogoutCleanup]);

  const { initializeAuth, handleOidcUserLoaded } = useAuthInitialization({
    processAuthParams,
    handleOidcLogin,
    restoreUserSession,
    logout,
    getRedirectPath,
    safeRedirect,
    setLoading,
  });

  const hasInitializedRef = useRef(false);
  const hasProcessedOidcAuthRef = useRef(false);
  const signingOutRef = useRef(false);

  const redirectAfterAuthentication = useCallback(
    (targetPath: string) => {
      safeRedirect(targetPath);

      if (window.location.pathname === targetPath) {
        sessionStorage.removeItem(SSO_AUTH_PROGRESS_KEY);
      }
    },
    [safeRedirect],
  );

  const handleSignInRequest = useCallback(
    async (authParams: AuthParamsSnapshot = readAuthParamsFromLocation()) => {
      const success = await processAuthParams(authParams);

      if (authParams.hasOidcCallback) {
        if (LocalStorage.getAccessToken()) {
          const targetPath = getRedirectPath();
          redirectAfterAuthentication(targetPath);
        }
        return true;
      }

      if (!success) {
        sessionStorage.removeItem(SSO_AUTH_PROGRESS_KEY);
      }

      return success;
    },
    [processAuthParams, getRedirectPath, redirectAfterAuthentication],
  );

  useEffect(() => {
    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      saveRedirectPath();
      initializeAuth();
    }

    oidcAuth.events?.addUserLoaded(handleOidcUserLoaded);
    return () => oidcAuth.events?.removeUserLoaded(handleOidcUserLoaded);
  }, [saveRedirectPath, initializeAuth, oidcAuth.events, handleOidcUserLoaded]);

  // Effect 2: Handle OIDC authentication state changes
  // When OIDC completes authentication, save tokens to localStorage
  useEffect(() => {
    const processOidcAuth = async () => {
      // Only process when OIDC is authenticated, has user, but no token saved yet
      if (
        oidcAuth.isAuthenticated &&
        oidcAuth.user &&
        !LocalStorage.getAccessToken() &&
        !hasProcessedOidcAuthRef.current
      ) {
        hasProcessedOidcAuthRef.current = true;

        try {
          const result = await handleOidcLogin(
            oidcAuth.user.access_token,
            oidcAuth.user.refresh_token,
          );

          if (result.shouldRedirect && LocalStorage.getAccessToken()) {
            const targetPath = getRedirectPath();
            redirectAfterAuthentication(targetPath);
          }
        } catch (error) {
          console.error("Failed to save OIDC tokens:", error);
          hasProcessedOidcAuthRef.current = false;
        }
      }
    };

    processOidcAuth();
  }, [
    oidcAuth.isAuthenticated,
    oidcAuth.user,
    handleOidcLogin,
    getRedirectPath,
    redirectAfterAuthentication,
  ]); // Watch for OIDC state changes

  const login = (userData: User, token: string, refreshToken?: string) => {
    handleManualLogin(userData, token, refreshToken);
  };

  const signinTpass = async () => {
    saveRedirectPath();
    requireAdditionalInfoOnNextLogin(undefined, { force: true });
    const redirectUri = cognitoAuthConfig.redirect_uri || window.location.origin;
    const clientId = cognitoAuthConfig.client_id;
    await handleTpassLogin(clientId, redirectUri);
  };

  const signOutRedirect = () => {
    // Ignore repeated triggers (e.g. a double click) while the revocation below is pending.
    if (signingOutRef.current) return;
    signingOutRef.current = true;

    void (async () => {
      try {
        saveRedirectPath();
        // Revoke before cleanup clears the refresh token, and give the request a bounded head
        // start before navigating away: keepalive requests that need a CORS preflight are not
        // supported by every browser, so keepalive alone cannot be relied on.
        await Promise.race([revokeRefreshToken(), wait(REVOKE_TIMEOUT_MS)]);
        performLogoutCleanup();

        const clientId = cognitoAuthConfig.client_id;
        const logoutUri = cognitoAuthConfig.redirect_uri || window.location.origin;
        const logoutUrl = `${EnvVariables.cognitoLogoutUrl}/logout?client_id=${encodeURIComponent(clientId)}&logout_uri=${encodeURIComponent(logoutUri)}`;

        window.location.href = logoutUrl;
      } catch (error) {
        // Allow another attempt; on success the guard stays set until the page navigates away.
        signingOutRef.current = false;
        console.error("Sign-out failed:", error);
      }
    })();
  };

  const signinRedirect = () => {
    saveRedirectPath();
    requireAdditionalInfoOnNextLogin(undefined, { force: true });
    sessionStorage.setItem(SSO_AUTH_PROGRESS_KEY, "true");
    oidcAuth.signinRedirect();
  };

  // Check both React state and sessionStorage for user to handle race conditions during login
  const hasStoredUser = hasValidStoredUserSession();
  const hasAccessToken = !!LocalStorage.getAccessToken();
  const isAuthenticated = hasAccessToken && (!!user || hasStoredUser || oidcAuth.isAuthenticated);

  return (
    <AuthContext.Provider
      value={{
        user,
        oidcUser: oidcAuth.user || null,
        isAuthenticated,
        loading: loading || oidcAuth.isLoading,
        login,
        logout,
        signinTpass,
        signOutRedirect,
        handleSignInRequest,
        signinRedirect,
        saveRedirectPath,
        getRedirectPath,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
